<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { InventoryBucket } from '@/composables/useYardInventory';

const props = defineProps<{
  modelValue: boolean;
  buckets: InventoryBucket[];
  selection: Set<string>;
}>();

const emit = defineEmits<{
  'update:modelValue': [open: boolean];
  apply: [next: Set<string>];
}>();

const { t } = useI18n();

const draft = ref<Set<string>>(new Set(props.selection));

watch(
  () => props.modelValue,
  open => {
    if (open) {
      draft.value = new Set(props.selection);
    }
  }
);

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
});

const unassignedBuckets = computed(() =>
  props.buckets.filter(bucket => bucket.kind === 'missing' || bucket.kind === 'waiting')
);
const areaBuckets = computed(() => props.buckets.filter(bucket => bucket.kind === 'area'));

const allKeys = computed(() => props.buckets.map(bucket => bucket.key));

const selectAllState = computed<'all' | 'some' | 'none'>(() => {
  const total = allKeys.value.length;
  const selected = allKeys.value.filter(key => draft.value.has(key)).length;
  if (selected === 0) {
    return 'none';
  }
  if (selected === total) {
    return 'all';
  }
  return 'some';
});

const selectAllIcon = computed(() => {
  switch (selectAllState.value) {
    case 'all':
      return 'mdi-checkbox-marked';
    case 'some':
      return 'mdi-minus-box';
    default:
      return 'mdi-checkbox-blank-outline';
  }
});

function isSelected(key: string) {
  return draft.value.has(key);
}

function toggleBucket(key: string) {
  const next = new Set(draft.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  draft.value = next;
}

function toggleSelectAll() {
  if (selectAllState.value === 'all') {
    draft.value = new Set();
  } else {
    draft.value = new Set(allKeys.value);
  }
}

function onDone() {
  emit('apply', new Set(draft.value));
  // Reset the local draft now that the selection is committed; it re-syncs from
  // `props.selection` on the next open via the `modelValue` watcher above.
  draft.value = new Set();
  isOpen.value = false;
}

function getBucketLabel(bucket: InventoryBucket) {
  if (bucket.kind === 'missing') {
    return t('yardInventory.filterDialog.missing');
  }
  if (bucket.kind === 'waiting') {
    return t('yardInventory.filterDialog.waitingAssignment');
  }
  return bucket.name;
}

function getBucketCountLabel(count: number) {
  return t('yardInventory.assetCount', { count });
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    fullscreen
    transition="slide-y-reverse-transition"
    scroll-strategy="none"
    data-testid="yard-inventory-filter-dialog">
    <v-card class="filter-dialog-card d-flex flex-column h-100 bg-surface rounded-0">
      <v-card-title class="d-flex align-center justify-space-between pa-4">
        <h2 class="typography-heading-md-bold ma-0">
          {{ t('yardInventory.filterDialog.title') }}
        </h2>
        <v-btn
          size="small"
          icon
          variant="text"
          :aria-label="t('yardInventory.filterDialog.close')"
          @click="isOpen = false">
          <v-icon icon="mdi-close" />
        </v-btn>
      </v-card-title>

      <v-divider />

      <div class="flex-fill overflow-y-auto">
        <button
          type="button"
          class="filter-row filter-row--select-all"
          :aria-pressed="selectAllState === 'all'"
          @click="toggleSelectAll">
          <v-icon
            size="22"
            :color="selectAllState === 'none' ? undefined : 'primary'"
            :icon="selectAllIcon" />
          <span class="typography-body-md">
            {{ t('yardInventory.filterDialog.selectAll') }}
          </span>
        </button>

        <template v-if="unassignedBuckets.length">
          <div class="filter-section-header">
            {{ t('yardInventory.filterDialog.unassignedHeader') }}
          </div>
          <button
            v-for="bucket in unassignedBuckets"
            :key="bucket.key"
            type="button"
            class="filter-row"
            :class="{ 'filter-row--selected': isSelected(bucket.key) }"
            :aria-pressed="isSelected(bucket.key)"
            :data-testid="`yard-inventory-filter-row-${bucket.key}`"
            @click="toggleBucket(bucket.key)">
            <v-icon
              size="22"
              :color="isSelected(bucket.key) ? 'primary' : undefined"
              :icon="
                isSelected(bucket.key) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
              " />
            <div class="d-flex flex-column align-start text-start flex-fill">
              <span class="typography-body-md">{{ getBucketLabel(bucket) }}</span>
              <span class="typography-caption text--color-text-secondary">
                {{ getBucketCountLabel(bucket.totalCount) }}
              </span>
            </div>
            <v-chip v-if="bucket.inScope" size="x-small" variant="flat" color="primary">
              <span class="typography-chip-sm">
                {{ t('yardInventory.filterDialog.taskScope') }}
              </span>
            </v-chip>
          </button>
        </template>

        <template v-if="areaBuckets.length">
          <div class="filter-section-header">
            {{ t('yardInventory.filterDialog.assignedHeader') }}
          </div>
          <button
            v-for="bucket in areaBuckets"
            :key="bucket.key"
            type="button"
            class="filter-row"
            :class="{ 'filter-row--selected': isSelected(bucket.key) }"
            :aria-pressed="isSelected(bucket.key)"
            :data-testid="`yard-inventory-filter-row-${bucket.key}`"
            @click="toggleBucket(bucket.key)">
            <v-icon
              size="22"
              :color="isSelected(bucket.key) ? 'primary' : undefined"
              :icon="
                isSelected(bucket.key) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
              " />
            <div class="d-flex flex-column align-start text-start flex-fill">
              <span class="typography-body-md">{{ bucket.name }}</span>
              <span class="typography-caption text--color-text-secondary">
                {{ getBucketCountLabel(bucket.totalCount) }}
              </span>
            </div>
            <v-chip v-if="bucket.inScope" size="x-small" variant="flat" color="primary">
              <span class="typography-chip-sm">
                {{ t('yardInventory.filterDialog.taskScope') }}
              </span>
            </v-chip>
          </button>
        </template>
      </div>

      <v-divider />

      <div class="pa-3">
        <v-btn
          block
          size="x-large"
          variant="outlined"
          class="typography-button-lg"
          data-testid="yard-inventory-filter-done"
          @click="onDone">
          {{ t('yardInventory.filterDialog.done') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.filter-dialog-card {
  height: calc(100dvh - $spacing-6) !important;
  min-height: calc(100dvh - $spacing-6) !important;
  max-height: calc(100dvh - $spacing-6) !important;
  top: $spacing-6;
  margin: 0;
}

.filter-section-header {
  padding: 0.75rem 1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $color-text-tertiary;
  background: $color-neutral-10;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  border-block-end: 1px solid $color-line-divider;
  background: $color-neutral-0;
  cursor: pointer;

  &:hover {
    background: $color-neutral-10;
  }
}

.filter-row--selected {
  background: $color-primary-20;

  &:hover {
    background: $color-primary-20;
  }
}

.filter-row--select-all {
  background: $color-neutral-0;
}
</style>
